import React, { useState, useRef } from 'react';
import { 
  FileText, 
  Calendar as CalendarIcon,
  User,
  UserCircle,
  Briefcase,
  Loader2,
  Download,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { generateLetterheadPDF } from '../../utils/pdfLetterhead';
import toast from 'react-hot-toast';

export const LetterheadCreatePage: React.FC = () => {
  const [date, setDate] = useState<string>('');
  const [recipientName, setRecipientName] = useState<string>('');
  const [letterContent, setLetterContent] = useState<string>('');
  const [senderName, setSenderName] = useState<string>('');
  const [roleDesignation, setRoleDesignation] = useState<string>('');
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [signatureFileName, setSignatureFileName] = useState<string>('');
  const signatureInputRef = useRef<HTMLInputElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  };

  const formatDateFromInput = (dateInput: string): string => {
    if (!dateInput) return '';
    try {
      const d = new Date(dateInput);
      return d.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateInput;
    }
  };

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setSignatureImage(result);
      setSignatureFileName(file.name);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSignature = () => {
    setSignatureImage(null);
    setSignatureFileName('');
    if (signatureInputRef.current) {
      signatureInputRef.current.value = '';
    }
  };

  const handleCreateLetterhead = async () => {
    // Validation
    if (!date.trim()) {
      toast.error('Please select a date');
      return;
    }
    if (!recipientName.trim()) {
      toast.error('Please enter recipient name');
      return;
    }
    if (!letterContent.trim()) {
      toast.error('Please enter letter content');
      return;
    }
    if (!senderName.trim()) {
      toast.error('Please enter sender name');
      return;
    }
    if (!roleDesignation.trim()) {
      toast.error('Please enter role/designation');
      return;
    }

    setIsGenerating(true);
    
    try {
      const formattedDate = formatDateFromInput(date);
      
      await generateLetterheadPDF({
        date: formattedDate,
        recipientName: recipientName.trim(),
        letterContent: letterContent.trim(),
        senderName: senderName.trim(),
        roleDesignation: roleDesignation.trim(),
        signatureImage: signatureImage || undefined
      });
      
      toast.success('Letterhead PDF downloaded successfully!');
      
      // Reset form
      setDate('');
      setRecipientName('');
      setLetterContent('');
      setSenderName('');
      setRoleDesignation('');
      setSignatureImage(null);
      setSignatureFileName('');
      if (signatureInputRef.current) {
        signatureInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error generating letterhead PDF:', error);
      toast.error(error.message || 'Failed to generate letterhead PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <FileText className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Create Letterhead</h1>
              <p className="text-sm text-gray-500">Generate professional letterhead documents</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={(e) => { e.preventDefault(); handleCreateLetterhead(); }}>
              <div className="space-y-6">
                {/* Date */}
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      id="date"
                      value={date}
                      onChange={handleDateChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Recipient Name */}
                <div>
                  <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="recipientName"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      required
                      placeholder="Enter recipient name"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Letter Content */}
                <div>
                  <label htmlFor="letterContent" className="block text-sm font-medium text-gray-700 mb-2">
                    Letter Head Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="letterContent"
                    value={letterContent}
                    onChange={(e) => setLetterContent(e.target.value)}
                    required
                    rows={8}
                    placeholder="Enter the letter content here..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                  />
                </div>

                {/* Sender Name */}
                <div>
                  <label htmlFor="senderName" className="block text-sm font-medium text-gray-700 mb-2">
                    Sender Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <UserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="senderName"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      required
                      placeholder="Enter sender name"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Signature Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signature Image (Optional)
                  </label>
                  <div className="space-y-3">
                    {signatureImage ? (
                      <div className="relative border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <ImageIcon className="h-5 w-5 text-primary-600" />
                            <span className="text-sm font-medium text-gray-700">
                              {signatureFileName || 'Signature Image'}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveSignature}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="flex items-center justify-center bg-white rounded border border-gray-200 p-3">
                          <img
                            src={signatureImage}
                            alt="Signature preview"
                            className="max-h-32 max-w-full object-contain"
                          />
                        </div>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 5MB)</p>
                        </div>
                        <input
                          ref={signatureInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleSignatureUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Role / Designation */}
                <div>
                  <label htmlFor="roleDesignation" className="block text-sm font-medium text-gray-700 mb-2">
                    Role / Designation <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="roleDesignation"
                      value={roleDesignation}
                      onChange={(e) => setRoleDesignation(e.target.value)}
                      required
                      placeholder="Enter role or designation"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isGenerating}
                    className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5" />
                        <span>Create Letter Head</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

